/**
 * RoamerConnectModule.ts — Module #6: the Roamer Connect™.
 *
 * A platform module (NOT a core engine): connectivity and arrival services —
 * connectivity profile, SIM/eSIM plans + comparison, internet intelligence,
 * arrival transport, essential utilities, Roamer Reports™, smart
 * recommendations, quick actions, and privacy controls. Module pattern:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound ONLY via Core bus events + manual entry:
 *    - LocationChanged ({ location: {...} } wrapped, flat fallback) → current
 *      city; a city change is an "arrival".
 *    - PlacesUpdated / CommunityUpdated are fusion-bridged metadata-only
 *      events ({domain, providerId, recordCount}) — refresh signals ONLY;
 *      no places or reports are ever invented from them.
 *  • Outbound: arrival briefings publish as NotificationSent kind
 *    'connectArrival' — but ONLY when locationSharingEnabled (privacy is
 *    opt-in; off by default). Plan-expiry alerts publish once per plan as
 *    kind 'connectPlanExpiring' regardless (they carry no location).
 *  • Honesty: internet intelligence and recommendations derive exclusively
 *    from recorded data with explicit basis; quick actions are available
 *    only when recorded data can back them, with a reason either way.
 *  • Providers (eSIM/SIM/pricing/coverage/transit/places) are a registry of
 *    interfaces only — future integrations, no implementations.
 *  • Persistence nomad:connect:v1 with record-by-record sanitization, caps,
 *    deep clones at read boundaries; export honours privacy settings.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  CONNECT_PROVIDER_KINDS,
  DATA_PLAN_TYPES,
  DEFAULT_CONNECTIVITY_PROFILE,
  DEFAULT_CONNECT_PRIVACY,
  QUICK_ACTION_KINDS,
  TRANSPORT_KINDS,
  UTILITY_KINDS,
  type ActivationGuide,
  type ArrivalRecord,
  type CityConnectivityFacts,
  type ConnectExport,
  type ConnectPlace,
  type ConnectPrivacySettings,
  type ConnectProvider,
  type ConnectRecommendation,
  type ConnectivityProfile,
  type DataPlan,
  type InternetIntelligence,
  type PlaceKind,
  type PlanComparison,
  type QuickAction,
  type RoamerReport,
  type SpeedReport,
} from './models';
import { computeInternetIntelligence } from './intelligence';
import { computeRecommendations } from './recommendations';

const STORAGE_KEY = 'nomad:connect:v1';
const MODULE_ID = 'roamer-connect-module';
const MAX_PLANS = 100;
const MAX_GUIDES = 100;
const MAX_SPEED_REPORTS = 500;
const MAX_CITY_FACTS = 200;
const MAX_PLACES = 1000;
const MAX_REPORTS = 500;
const MAX_TRAVEL_HISTORY = 200;

const ALL_PLACE_KINDS: PlaceKind[] = [...TRANSPORT_KINDS, ...UTILITY_KINDS];

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

function asNumberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function asPositiveOrNull(v: unknown): number | null {
  const n = asNumberOrNull(v);
  return n != null && n > 0 ? n : null;
}

// ── Provider registry (future — interfaces only) ─────────────────────────────

const providers = new Map<string, ConnectProvider>();

export function registerConnectProvider(p: ConnectProvider): boolean {
  if (providers.has(p.id) || !CONNECT_PROVIDER_KINDS.includes(p.kind)) return false;
  providers.set(p.id, p);
  return true;
}

export function getConnectProviders(): ConnectProvider[] {
  return Array.from(providers.values());
}

interface PersistedState {
  currentCity: string | null;
  currentCountry: string | null;
  profile: ConnectivityProfile;
  privacy: ConnectPrivacySettings;
  plans: DataPlan[];
  guides: ActivationGuide[];
  speedReports: SpeedReport[];
  cityFacts: CityConnectivityFacts[];
  places: ConnectPlace[];
  reports: RoamerReport[];
  travelHistory: ArrivalRecord[];
  expiryNotifiedPlanIds: string[];
  refreshSignalCount: number;
}

export type NewDataPlanInput = Omit<DataPlan, 'id' | 'addedAt'>;
export type NewPlaceInput = Omit<ConnectPlace, 'id' | 'addedAt' | 'saved'> & { saved?: boolean };

export class RoamerConnectModule {
  private currentCity: string | null = null;
  private currentCountry: string | null = null;
  private profile: ConnectivityProfile = { ...DEFAULT_CONNECTIVITY_PROFILE };
  private privacy: ConnectPrivacySettings = { ...DEFAULT_CONNECT_PRIVACY };
  private plans: DataPlan[] = [];
  private guides: ActivationGuide[] = [];
  private speedReports: SpeedReport[] = [];
  private cityFacts: CityConnectivityFacts[] = [];
  private places: ConnectPlace[] = [];
  private reports: RoamerReport[] = [];
  private travelHistory: ArrivalRecord[] = [];
  private expiryNotifiedPlanIds = new Set<string>();
  private refreshSignalCount = 0;
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  private listeners = new Set<() => void>();

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration ────────────────────────────────────────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Connect™',
      version: '1.0.0',
      capabilities: ['connectivity-profile', 'sim-esim', 'internet-intelligence', 'arrival-services', 'quick-actions'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const safe = (handler: (e: CoreEvent) => void) => (e: CoreEvent) => {
      try {
        handler(e);
      } catch {
        /* a bad payload must never break the bus */
      }
    };
    this.unsubscribes.push(core.on('LocationChanged', safe((e) => this.onLocationChanged(e)), 'module:connect'));
    this.unsubscribes.push(core.on('PlacesUpdated', safe(() => this.onRefreshSignal()), 'module:connect'));
    this.unsubscribes.push(core.on('CommunityUpdated', safe(() => this.onRefreshSignal()), 'module:connect'));
    // ECOSYSTEM SYNCHRONIZATION™ (Build 2.04.0): accommodation changes are a
    // refresh signal only — Stay™ owns the records, Connect™ re-reads nothing
    // from the payload (metadata-only contract).
    this.unsubscribes.push(core.on('HousingUpdated', safe(() => this.onRefreshSignal()), 'module:connect'));
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  private publishNotification(kind: string, payload: Record<string, unknown>, at: number): void {
    try {
      this.core?.publish('NotificationSent', MODULE_ID, { kind, ...payload }, at);
    } catch {
      /* best-effort */
    }
  }

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const city = asString(p.city);
    if (!city || city === this.currentCity) return;
    this.currentCity = city;
    this.currentCountry = asString(p.country);
    // Arrival: history only with opt-in; briefing on the bus only with opt-in.
    if (this.privacy.trackTravelHistory) {
      this.travelHistory.push({ city, country: this.currentCountry, arrivedAt: e.at });
      if (this.travelHistory.length > MAX_TRAVEL_HISTORY) {
        this.travelHistory = this.travelHistory.slice(-MAX_TRAVEL_HISTORY);
      }
    }
    if (this.privacy.locationSharingEnabled) {
      this.publishNotification('connectArrival', { city, country: this.currentCountry }, e.at);
    }
    this.afterUpdate();
  }

  private onRefreshSignal(): void {
    // Fusion events carry metadata only — a refresh signal, never data.
    this.refreshSignalCount += 1;
    this.afterUpdate();
  }

  getRefreshSignalCount(): number {
    return this.refreshSignalCount;
  }

  /** Read-only: whether the module is currently attached to Roamer Core. */
  isAttached(): boolean {
    return this.core != null;
  }

  getCurrentCity(): string | null {
    return this.currentCity;
  }

  // ── Connectivity profile ────────────────────────────────────────────────────

  getProfile(): ConnectivityProfile {
    return structuredClone(this.profile);
  }

  updateProfile(patch: Partial<ConnectivityProfile>): ConnectivityProfile {
    this.profile = { ...this.profile, ...patch };
    this.afterUpdate();
    return this.getProfile();
  }

  // ── Plans, guides, comparison ───────────────────────────────────────────────

  addPlan(input: NewDataPlanInput, at: number = Date.now()): DataPlan | null {
    if (!asString(input.provider) || !asString(input.name) || !asString(input.country)) return null;
    if (!DATA_PLAN_TYPES.includes(input.type)) return null;
    const plan: DataPlan = {
      ...input,
      provider: input.provider.trim(),
      name: input.name.trim(),
      country: input.country.trim(),
      priceMonthly: asPositiveOrNull(input.priceMonthly),
      dataGb: asPositiveOrNull(input.dataGb),
      speedMbps: asPositiveOrNull(input.speedMbps),
      expiresAt: asNumberOrNull(input.expiresAt),
      currency: asString(input.currency) ?? 'USD',
      notes: input.notes ?? '',
      id: `plan-${at}-${this.plans.length}`,
      addedAt: at,
    };
    this.plans.push(plan);
    if (this.plans.length > MAX_PLANS) this.plans = this.plans.slice(-MAX_PLANS);
    this.afterUpdate();
    return structuredClone(plan);
  }

  updatePlan(id: string, patch: Partial<Omit<DataPlan, 'id' | 'addedAt'>>): boolean {
    const plan = this.plans.find((p) => p.id === id);
    if (!plan) return false;
    Object.assign(plan, patch);
    this.afterUpdate();
    return true;
  }

  getPlans(): DataPlan[] {
    return structuredClone(this.plans);
  }

  /**
   * Once-only expiry alerts for ACTIVE plans expiring within 3 days (or past
   * due). Deterministic: call with the current time; each plan alerts once.
   */
  checkPlanExpiry(now: number = Date.now()): void {
    const soon = 3 * 24 * 60 * 60 * 1000;
    let changed = false;
    for (const plan of this.plans) {
      if (!plan.active || plan.expiresAt == null) continue;
      if (plan.expiresAt - now > soon) continue;
      if (this.expiryNotifiedPlanIds.has(plan.id)) continue;
      this.expiryNotifiedPlanIds.add(plan.id);
      this.publishNotification('connectPlanExpiring', {
        planId: plan.id, provider: plan.provider, name: plan.name, expiresAt: plan.expiresAt,
      }, now);
      changed = true;
    }
    if (changed) this.afterUpdate();
  }

  addActivationGuide(input: Omit<ActivationGuide, 'id' | 'addedAt'>, at: number = Date.now()): ActivationGuide | null {
    if (!asString(input.provider) || !asString(input.title) || !asString(input.source)) return null;
    if (!Array.isArray(input.steps) || input.steps.length === 0) return null;
    const guide: ActivationGuide = { ...input, id: `guide-${at}-${this.guides.length}`, addedAt: at };
    this.guides.push(guide);
    if (this.guides.length > MAX_GUIDES) this.guides = this.guides.slice(-MAX_GUIDES);
    this.afterUpdate();
    return structuredClone(guide);
  }

  getActivationGuides(provider?: string): ActivationGuide[] {
    const guides = provider ? this.guides.filter((g) => g.provider === provider) : this.guides;
    return structuredClone(guides);
  }

  comparePlans(planIds: string[]): PlanComparison | null {
    const plans = planIds
      .map((id) => this.plans.find((p) => p.id === id))
      .filter((p): p is DataPlan => p != null);
    if (plans.length < 2) return null;
    // Cheapest only when every price is recorded in a single currency.
    let cheapest: string | null = null;
    const currencies = new Set(plans.map((p) => p.currency));
    if (currencies.size === 1 && plans.every((p) => p.priceMonthly != null)) {
      const best = Math.min(...plans.map((p) => p.priceMonthly!));
      const winners = plans.filter((p) => p.priceMonthly === best);
      cheapest = winners.length === 1 ? `${winners[0].provider} ${winners[0].name}` : null;
    }
    return structuredClone({
      planIds: plans.map((p) => p.id),
      providers: plans.map((p) => p.provider),
      rows: [
        { label: 'Plan', values: plans.map((p) => p.name) },
        { label: 'Type', values: plans.map((p) => p.type as string) },
        { label: 'Country', values: plans.map((p) => p.country) },
        { label: 'Price (monthly)', values: plans.map((p) => p.priceMonthly) },
        { label: 'Currency', values: plans.map((p) => p.currency) },
        { label: 'Data (GB)', values: plans.map((p) => p.dataGb) },
        { label: 'Speed (Mbps)', values: plans.map((p) => p.speedMbps) },
        { label: 'Active', values: plans.map((p) => p.active) },
      ],
      cheapest,
    });
  }

  // ── Internet intelligence ───────────────────────────────────────────────────

  addSpeedReport(input: Omit<SpeedReport, 'id'>, at: number = Date.now()): SpeedReport | null {
    if (!asString(input.city) || !(input.downloadMbps > 0)) return null;
    const report: SpeedReport = { ...input, id: `speed-${at}-${this.speedReports.length}` };
    this.speedReports.push(report);
    if (this.speedReports.length > MAX_SPEED_REPORTS) {
      this.speedReports = this.speedReports.slice(-MAX_SPEED_REPORTS);
    }
    this.afterUpdate();
    return structuredClone(report);
  }

  setCityFacts(input: Omit<CityConnectivityFacts, 'updatedAt'>, at: number = Date.now()): CityConnectivityFacts | null {
    if (!asString(input.city)) return null;
    const facts: CityConnectivityFacts = { ...input, updatedAt: at };
    this.cityFacts = this.cityFacts.filter((f) => f.city !== input.city);
    this.cityFacts.push(facts);
    if (this.cityFacts.length > MAX_CITY_FACTS) this.cityFacts = this.cityFacts.slice(-MAX_CITY_FACTS);
    this.afterUpdate();
    return structuredClone(facts);
  }

  getInternetIntelligence(city?: string): InternetIntelligence | null {
    const target = city ?? this.currentCity;
    if (!target) return null;
    return structuredClone(computeInternetIntelligence(
      target,
      this.speedReports,
      this.cityFacts.find((f) => f.city === target) ?? null,
    ));
  }

  // ── Arrival services & utilities ────────────────────────────────────────────

  addPlace(input: NewPlaceInput, at: number = Date.now()): ConnectPlace | null {
    if (!asString(input.name) || !asString(input.city)) return null;
    if (!ALL_PLACE_KINDS.includes(input.kind)) return null;
    const place: ConnectPlace = {
      ...input,
      name: input.name.trim(),
      city: input.city.trim(),
      address: asString(input.address),
      url: asString(input.url),
      notes: input.notes ?? '',
      saved: input.saved === true,
      id: `place-${at}-${this.places.length}`,
      addedAt: at,
    };
    this.places.push(place);
    if (this.places.length > MAX_PLACES) this.places = this.places.slice(-MAX_PLACES);
    this.afterUpdate();
    return structuredClone(place);
  }

  /** "Save Utility" quick action. */
  savePlace(id: string, saved: boolean = true): boolean {
    const place = this.places.find((p) => p.id === id);
    if (!place) return false;
    place.saved = saved;
    this.afterUpdate();
    return true;
  }

  getPlaces(filter?: { city?: string; kind?: PlaceKind; savedOnly?: boolean }): ConnectPlace[] {
    let list = this.places;
    if (filter?.city) list = list.filter((p) => p.city === filter.city);
    if (filter?.kind) list = list.filter((p) => p.kind === filter.kind);
    if (filter?.savedOnly) list = list.filter((p) => p.saved);
    return structuredClone(list);
  }

  // ── Community insights (Roamer Reports™) ────────────────────────────────────

  addRoamerReport(input: Omit<RoamerReport, 'id'>, at: number = Date.now()): RoamerReport | null {
    if (!asString(input.city) || !asString(input.topic) || !asString(input.source)) return null;
    const report: RoamerReport = { ...input, id: `report-${at}-${this.reports.length}` };
    this.reports.push(report);
    if (this.reports.length > MAX_REPORTS) this.reports = this.reports.slice(-MAX_REPORTS);
    this.afterUpdate();
    return structuredClone(report);
  }

  getRoamerReports(city?: string, verifiedOnly: boolean = false): RoamerReport[] {
    let list = city ? this.reports.filter((r) => r.city === city) : this.reports;
    if (verifiedOnly) list = list.filter((r) => r.verified);
    return structuredClone(list);
  }

  // ── Smart recommendations & quick actions ──────────────────────────────────

  getRecommendations(now: number = Date.now()): ConnectRecommendation[] {
    return structuredClone(computeRecommendations({
      profile: this.profile,
      plans: this.plans,
      cityFacts: this.cityFacts,
      speedReports: this.speedReports,
      places: this.places,
      currentCity: this.currentCity,
      now,
    }));
  }

  /** Quick actions are honest: available only when recorded data backs them. */
  getQuickActions(): QuickAction[] {
    const city = this.currentCity;
    const inCity = (kind: PlaceKind) =>
      city != null && this.places.some((p) => p.city === city && p.kind === kind);
    const cityLabel = city ?? 'your city (unknown)';
    const placeAction = (
      kind: QuickAction['kind'],
      label: string,
      placeKind: PlaceKind,
      noun: string,
    ): QuickAction => ({
      kind,
      label,
      available: inCity(placeKind),
      reason: inCity(placeKind)
        ? `${noun} recorded in ${cityLabel}`
        : `no ${noun.toLowerCase()} recorded in ${cityLabel}`,
    });
    const esimPlans = this.plans.filter((p) => p.type === 'esim');
    const actions: QuickAction[] = [
      {
        kind: 'compareEsims',
        label: 'Compare eSIMs',
        available: esimPlans.length >= 2,
        reason: `${esimPlans.length} eSIM plan${esimPlans.length === 1 ? '' : 's'} recorded`,
      },
      placeAction('findAtm', 'Find ATM', 'atm', 'ATM'),
      placeAction('navigateToEmbassy', 'Navigate to Embassy', 'embassy', 'Embassy'),
      placeAction('openTransitMap', 'Open Transit Map', 'publicTransit', 'Public transit'),
      placeAction('bookAirportRide', 'Book Airport Ride', 'airportTransport', 'Airport transport'),
      placeAction('locatePharmacy', 'Locate Pharmacy', 'pharmacy', 'Pharmacy'),
      placeAction('findCoworking', 'Find Coworking', 'coworking', 'Coworking'),
      {
        kind: 'saveUtility',
        label: 'Save Utility',
        available: this.places.length > 0,
        reason: `${this.places.length} place${this.places.length === 1 ? '' : 's'} recorded`,
      },
    ];
    void QUICK_ACTION_KINDS;
    return actions;
  }

  // ── Privacy, export, delete ─────────────────────────────────────────────────

  getPrivacySettings(): ConnectPrivacySettings {
    return { ...this.privacy };
  }

  updatePrivacySettings(patch: Partial<ConnectPrivacySettings>): ConnectPrivacySettings {
    this.privacy = { ...this.privacy, ...patch };
    // Turning history off deletes what was collected — control means control.
    if (patch.trackTravelHistory === false) this.travelHistory = [];
    this.afterUpdate();
    return this.getPrivacySettings();
  }

  getTravelHistory(): ArrivalRecord[] {
    return structuredClone(this.travelHistory);
  }

  exportConnect(now: number = Date.now()): ConnectExport {
    return structuredClone({
      exportedAt: now,
      profile: this.privacy.shareConnectivityPreferences ? this.profile : null,
      plans: this.plans,
      guides: this.guides,
      speedReports: this.speedReports,
      cityFacts: this.cityFacts,
      places: this.places,
      reports: this.reports,
      travelHistory: this.privacy.trackTravelHistory ? this.travelHistory : [],
    });
  }

  deleteAll(): void {
    this.currentCity = null;
    this.currentCountry = null;
    this.profile = { ...DEFAULT_CONNECTIVITY_PROFILE };
    this.privacy = { ...DEFAULT_CONNECT_PRIVACY };
    this.plans = [];
    this.guides = [];
    this.speedReports = [];
    this.cityFacts = [];
    this.places = [];
    this.reports = [];
    this.travelHistory = [];
    this.expiryNotifiedPlanIds = new Set();
    this.refreshSignalCount = 0;
    this.persist();
    this.emitChange();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(): void {
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        currentCity: this.currentCity,
        currentCountry: this.currentCountry,
        profile: this.profile,
        privacy: this.privacy,
        plans: this.plans,
        guides: this.guides,
        speedReports: this.speedReports,
        cityFacts: this.cityFacts,
        places: this.places,
        reports: this.reports,
        travelHistory: this.travelHistory,
        expiryNotifiedPlanIds: Array.from(this.expiryNotifiedPlanIds),
        refreshSignalCount: this.refreshSignalCount,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('connect', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Called by hydrateConnectFromCloud when a newer cloud copy is available. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(_now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      this.currentCity = asString(state.currentCity);
      this.currentCountry = asString(state.currentCountry);

      const pr = (state.profile ?? {}) as Record<string, unknown>;
      this.profile = {
        currentCarrier: asString(pr.currentCarrier),
        preferredEsimProvider: asString(pr.preferredEsimProvider),
        preferredSimProvider: asString(pr.preferredSimProvider),
        preferredDataPlan: asString(pr.preferredDataPlan),
        minInternetMbps: asPositiveOrNull(pr.minInternetMbps),
        backupPreference: pr.backupPreference === 'secondSim' || pr.backupPreference === 'esim' ||
          pr.backupPreference === 'pocketWifi' || pr.backupPreference === 'tethering' ||
          pr.backupPreference === 'none' ? pr.backupPreference : null,
        wifiPreference: pr.wifiPreference === 'coworking' || pr.wifiPreference === 'cafe' ||
          pr.wifiPreference === 'homeFiber' || pr.wifiPreference === 'mobileOnly' ? pr.wifiPreference : null,
        roamingStatus: pr.roamingStatus === 'roaming' || pr.roamingStatus === 'local' ? pr.roamingStatus : 'unknown',
      };

      const pv = (state.privacy ?? {}) as Record<string, unknown>;
      this.privacy = {
        locationSharingEnabled: pv.locationSharingEnabled === true,
        shareConnectivityPreferences: pv.shareConnectivityPreferences === true,
        trackTravelHistory: pv.trackTravelHistory === true,
      };

      this.plans = (Array.isArray(state.plans) ? state.plans : [])
        .filter((p): p is Record<string, unknown> =>
          p != null && typeof p === 'object' &&
          typeof (p as { id?: unknown }).id === 'string' &&
          typeof (p as { provider?: unknown }).provider === 'string' &&
          typeof (p as { name?: unknown }).name === 'string' &&
          DATA_PLAN_TYPES.includes((p as { type?: never }).type as never))
        .map((p): DataPlan => ({
          id: p.id as string,
          provider: p.provider as string,
          name: p.name as string,
          type: p.type as DataPlan['type'],
          country: asString(p.country) ?? 'Unknown',
          priceMonthly: asPositiveOrNull(p.priceMonthly),
          currency: asString(p.currency) ?? 'USD',
          dataGb: asPositiveOrNull(p.dataGb),
          speedMbps: asPositiveOrNull(p.speedMbps),
          expiresAt: asNumberOrNull(p.expiresAt),
          active: p.active === true,
          notes: typeof p.notes === 'string' ? p.notes : '',
          addedAt: asNumberOrNull(p.addedAt) ?? 0,
        }))
        .slice(-MAX_PLANS);

      this.guides = (Array.isArray(state.guides) ? state.guides : [])
        .filter((g): g is ActivationGuide =>
          g != null && typeof g === 'object' &&
          typeof (g as ActivationGuide).id === 'string' &&
          typeof (g as ActivationGuide).provider === 'string' &&
          typeof (g as ActivationGuide).title === 'string' &&
          typeof (g as ActivationGuide).source === 'string' &&
          Array.isArray((g as ActivationGuide).steps) &&
          (g as ActivationGuide).steps.every((s) => typeof s === 'string'))
        .slice(-MAX_GUIDES);

      this.speedReports = (Array.isArray(state.speedReports) ? state.speedReports : [])
        .filter((r): r is SpeedReport =>
          r != null && typeof r === 'object' &&
          typeof (r as SpeedReport).id === 'string' &&
          typeof (r as SpeedReport).city === 'string' &&
          typeof (r as SpeedReport).downloadMbps === 'number' &&
          (r as SpeedReport).downloadMbps > 0)
        .map((r) => ({
          ...r,
          verified: r.verified === true,
          venue: asString(r.venue),
          reportedAt: asNumberOrNull(r.reportedAt) ?? 0,
        }))
        .slice(-MAX_SPEED_REPORTS);

      this.cityFacts = (Array.isArray(state.cityFacts) ? state.cityFacts : [])
        .filter((f): f is Record<string, unknown> =>
          f != null && typeof f === 'object' && typeof (f as { city?: unknown }).city === 'string')
        .map((f): CityConnectivityFacts => ({
          city: f.city as string,
          mobileCoverageNote: asString(f.mobileCoverageNote),
          fiberAvailable: typeof f.fiberAvailable === 'boolean' ? f.fiberAvailable : null,
          powerReliabilityNote: asString(f.powerReliabilityNote),
          publicWifiNote: asString(f.publicWifiNote),
          updatedAt: asNumberOrNull(f.updatedAt) ?? 0,
        }))
        .slice(-MAX_CITY_FACTS);

      this.places = (Array.isArray(state.places) ? state.places : [])
        .filter((p): p is Record<string, unknown> =>
          p != null && typeof p === 'object' &&
          typeof (p as { id?: unknown }).id === 'string' &&
          typeof (p as { name?: unknown }).name === 'string' &&
          typeof (p as { city?: unknown }).city === 'string' &&
          ALL_PLACE_KINDS.includes((p as { kind?: never }).kind as never))
        .map((p): ConnectPlace => ({
          id: p.id as string,
          kind: p.kind as PlaceKind,
          name: p.name as string,
          city: p.city as string,
          address: asString(p.address),
          url: asString(p.url),
          notes: typeof p.notes === 'string' ? p.notes : '',
          saved: p.saved === true,
          addedAt: asNumberOrNull(p.addedAt) ?? 0,
        }))
        .slice(-MAX_PLACES);

      this.reports = (Array.isArray(state.reports) ? state.reports : [])
        .filter((r): r is RoamerReport =>
          r != null && typeof r === 'object' &&
          typeof (r as RoamerReport).id === 'string' &&
          typeof (r as RoamerReport).city === 'string' &&
          typeof (r as RoamerReport).topic === 'string' &&
          typeof (r as RoamerReport).source === 'string' &&
          (r as RoamerReport).source.trim().length > 0)
        .map((r) => ({
          ...r,
          summary: typeof r.summary === 'string' ? r.summary : '',
          verified: r.verified === true,
          reportedAt: asNumberOrNull(r.reportedAt) ?? 0,
        }))
        .slice(-MAX_REPORTS);

      // Travel history only survives when the opt-in is still on.
      this.travelHistory = this.privacy.trackTravelHistory
        ? (Array.isArray(state.travelHistory) ? state.travelHistory : [])
            .filter((a): a is Record<string, unknown> =>
              a != null && typeof a === 'object' && typeof (a as { city?: unknown }).city === 'string')
            .map((a): ArrivalRecord => ({
              city: a.city as string,
              country: asString(a.country),
              arrivedAt: asNumberOrNull(a.arrivedAt) ?? 0,
            }))
            .slice(-MAX_TRAVEL_HISTORY)
        : [];

      this.expiryNotifiedPlanIds = new Set(
        (Array.isArray(state.expiryNotifiedPlanIds) ? state.expiryNotifiedPlanIds : [])
          .filter((id): id is string => typeof id === 'string'),
      );
      const count = asNumberOrNull(state.refreshSignalCount);
      this.refreshSignalCount = count != null && count >= 0 ? count : 0;
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerConnectModule = new RoamerConnectModule();

/**
 * Boot hydration: fetches the cloud copy of the Connect™ state if it is
 * strictly newer than the last local write, writes it to localStorage, and
 * signals the module to reload.  Fire-and-forget — boot never blocks the network.
 */
export async function hydrateConnectFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<unknown>('connect', 'state');
  if (!cloud) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('nomad:connect:v1', JSON.stringify(cloud));
    }
  } catch { return false; }
  roamerConnectModule.reloadFromStorage();
  return true;
}
